import { Upload, Loader2, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import type { AttachmentLabel } from "@/utils/attachmentLabels";
import { DOCUMENT_ACCEPT } from "@/utils/uploadFileTypes";

interface RequiredDoc {
  id: number;
  referenceKey: string;
  isPending: boolean;
  isBlocking: boolean;
  /** Info-only entry: pure checklist, never blocks a move, never warns. */
  isInfo?: boolean;
  /** Stage the requirement belongs to (server sends the stage name). */
  stage?: string;
}

interface RequiredDocumentsProps {
  requiredDocs: RequiredDoc[];
  uploadedDocs: any[];
  uploadProps: {
    attachedTo: string;
    attachedScope: string;
    category: string;
    folderId?: number | null;
  };
  /** Label for required-doc uploads (defaults to work file) */
  attachmentLabel?: AttachmentLabel;
  onSuccess: () => void;
  /**
   * Decide the lead-or-deal grain before uploading — same gate the dropzone
   * uses, so a checklist upload is filed the same way a dragged one is.
   * Resolves to `null` when the person cancels the prompt.
   */
  resolveScope?: (
    fileCount: number,
    firstFileName: string
  ) => Promise<{ childId: number | null } | null>;
}

export default function RequiredDocuments({
  requiredDocs,
  uploadedDocs,
  uploadProps,
  attachmentLabel = "WORKFILE",
  onSuccess,
  resolveScope,
}: RequiredDocumentsProps) {
  const { uploadFile } = useDocumentMutaion();

  if (!requiredDocs || requiredDocs.length === 0) return null;

  const isDocUploaded = (referenceKey: string) =>
    uploadedDocs?.some(
      (doc: any) => doc.name?.toLowerCase() === referenceKey.toLowerCase()
    );

  const handleFileUpload = async (file: File | null, referenceKey: string) => {
    if (!file) return;

    const scope = resolveScope ? await resolveScope(1, file.name) : null;
    if (resolveScope && !scope) return; // cancelled — nothing uploads

    uploadFile.mutate(
      {
        file,
        ...uploadProps,
        name: referenceKey,
        attachmentLabel,
        ...(scope ? { childId: scope.childId } : {}),
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  return (
    <div className="flex flex-col gap-3 m-0">
      <p
        role="heading"
        aria-level={3}
        className="m-0 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-black-8)]"
      >
        Required documents
      </p>
      <div className="flex flex-col gap-2">
        {requiredDocs.map((req) => {
          const uploaded = isDocUploaded(req.referenceKey);
          // Info-only entries are a calm checklist — no alarm tint, no
          // warning icon; they never gate anything.
          const infoOnly = !!req.isInfo;

          return (
            <div
              key={req.id}
              className={`flex items-center gap-3 rounded-[12px] px-3 py-3 transition-colors ${
                uploaded
                  ? "bg-green-50/50"
                  : infoOnly
                    ? "bg-blue-50/30"
                    : "bg-orange-50/30"
              }`}
            >
              {uploaded ? (
                <CheckCircle2 className="size-5 text-green-500 shrink-0" />
              ) : infoOnly ? (
                <Info className="size-5 text-blue-400 shrink-0" />
              ) : (
                <AlertCircle className="size-5 text-orange-500 shrink-0" />
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[14px] font-medium text-gray-900 truncate">
                    {req.referenceKey}
                  </p>
                  {infoOnly ? (
                    <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full leading-none">
                      Info
                    </span>
                  ) : (
                    req.isBlocking && (
                      <span className="text-[10px] font-medium bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full leading-none">
                        Blocking
                      </span>
                    )
                  )}
                </div>
                <p className="text-[12px] text-gray-500">
                  {[req.stage, uploaded ? "Uploaded" : "Not uploaded yet"]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>

              {!uploaded && (
                <label className="shrink-0 cursor-pointer">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-[12px] font-medium">
                    {uploadFile.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Upload className="size-3.5" />
                    )}
                    Upload
                  </div>
                  <Input
                    type="file"
                    className="hidden"
                    accept={DOCUMENT_ACCEPT}
                    onChange={(e) => {
                      // Capture BEFORE the reset: the handler awaits the
                      // lead-or-deal answer, and clearing the input empties
                      // the FileList behind it.
                      const file = e.target.files?.[0] ?? null;
                      e.target.value = "";
                      void handleFileUpload(file, req.referenceKey);
                    }}
                    disabled={uploadFile.isPending}
                  />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
